/**
 * PWA boot. Loaded from index.html as a second module entry, after the app.
 *
 * It is a separate entry rather than a call inside main.tsx so that nothing
 * about installing, updating or being offline is tangled into the React tree.
 * The overlay it puts on screen is plain DOM appended to the body, so it
 * cannot cause a re-render and does not depend on the app having mounted.
 *
 * Registration is production-only. A service worker on a dev server caches a
 * build that changes every keystroke, and the hour spent working out why an
 * edit "did nothing" is a cost this saves once per developer per project. In
 * dev it does the opposite job — it removes any worker a previous production
 * build left on the same origin.
 */
import "@tc/ui/styles/ui.css";
import {
  clearInstallOffer,
  installDismissed,
  markServedFromCache,
  markServedFromNetwork,
  offerInstall,
  offerUpdate,
  watchConnection,
} from "./overlay";

/** Not in lib.dom yet; this is the shape Chromium actually fires. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Long enough that the offer is never the first thing a visitor deals with. */
const INSTALL_OFFER_DELAY_MS = 15_000;

if (import.meta.env.PROD) {
  watchConnection();
  registerWorker();
  watchForInstall();
} else if ("serviceWorker" in navigator) {
  void navigator.serviceWorker
    .getRegistrations()
    .then((all) => Promise.all(all.map((r) => r.unregister())))
    .catch(() => undefined);
}

// ── The worker ──────────────────────────────────────────────────────

function registerWorker(): void {
  if (!("serviceWorker" in navigator)) return;

  /*
   * Reload exactly once, and only because someone asked.
   *
   * `controllerchange` also fires the first time a worker takes control of a
   * page that had none, and reloading on that would reload every first visit.
   */
  let reloadWhenSwapped = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!reloadWhenSwapped) return;
    reloadWhenSwapped = false;
    location.reload();
  });

  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const data: unknown = event.data;
    if (!data || typeof data !== "object") return;
    const message = data as { type?: string; source?: string; at?: string | null };
    if (message.type !== "TC_DATA_SOURCE") return;
    if (message.source === "cache") markServedFromCache(message.at ?? null);
    else if (message.source === "network") markServedFromNetwork();
  });

  void navigator.serviceWorker
    // `updateViaCache: "none"` so the worker script itself is never answered
    // from the HTTP cache. A worker that can be served stale is a worker that
    // can pin an old build indefinitely, which is the one PWA failure a reader
    // cannot get out of.
    .register("/sw.js", { scope: "/", updateViaCache: "none" })
    .then((registration) => {
      const apply = (worker: ServiceWorker) => () => {
        reloadWhenSwapped = true;
        worker.postMessage({ type: "TC_SKIP_WAITING" });
      };

      // Installed on a previous visit and still waiting.
      if (registration.waiting && navigator.serviceWorker.controller) {
        offerUpdate(apply(registration.waiting));
      }

      registration.addEventListener("updatefound", () => {
        const incoming = registration.installing;
        if (!incoming) return;
        incoming.addEventListener("statechange", () => {
          // A controller already exists, so this is an update rather than the
          // first install — the first install must never prompt anyone.
          if (incoming.state === "installed" && navigator.serviceWorker.controller) {
            offerUpdate(apply(incoming));
          }
        });
      });

      // A phone app is left open for days. Checking when the tab comes back is
      // what makes a deploy reach it at all.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void registration.update().catch(() => undefined);
      });
    })
    .catch(() => {
      // A failed registration must never break the app: everything the worker
      // does is an improvement on a page that already works without it.
    });
}

// ── Installation ────────────────────────────────────────────────────

function watchForInstall(): void {
  let deferred: InstallPromptEvent | null = null;

  addEventListener("beforeinstallprompt", (event) => {
    // Chromium's own mini-infobar is the interstitial we do not want.
    event.preventDefault();
    deferred = event as InstallPromptEvent;

    if (installDismissed()) return;
    // Never during sign-in or sign-up. Nobody installs an app they have not
    // been let into yet, and an offer there is pure friction on the one screen
    // that has to be frictionless.
    if (/^\/(signin|signup)\b/.test(location.pathname)) return;

    setTimeout(() => {
      if (!deferred || installDismissed()) return;
      offerInstall(() => void deferred?.prompt());
    }, INSTALL_OFFER_DELAY_MS);
  });

  addEventListener("appinstalled", () => {
    deferred = null;
    clearInstallOffer();
  });
}
